import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

type ToastTone = 'ok' | 'error' | 'info';
type ToastItem = { id: number; tone: ToastTone; message: ReactNode };

type ToastApi = {
  show: (message: ReactNode, tone?: ToastTone) => void;
  ok: (message: ReactNode) => void;
  error: (message: ReactNode) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const ICON = { ok: CheckCircle2, error: AlertCircle, info: Info };

/** Transient notifications, announced politely to screen readers. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const show = useCallback(
    (message: ReactNode, tone: ToastTone = 'info') => {
      const id = next.current++;
      setToasts((t) => [...t.slice(-3), { id, tone, message }]);
      window.setTimeout(() => dismiss(id), tone === 'error' ? 7000 : 4200);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({ show, ok: (m) => show(m, 'ok'), error: (m) => show(m, 'error') }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="ui-toasts" role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => {
          const Icon = ICON[t.tone];
          return (
            <div key={t.id} className={`ui-toast ui-toast--${t.tone}`}>
              <Icon aria-hidden />
              <div className="ui-toast__text">{t.message}</div>
              <button type="button" className="ui-copy ui-toast__close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
                <X aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
